import { Vector3 } from '@technology/web3d';
import type { PointIcon } from '@technology/web3d-plugin-icons';
import { stringToSeed } from '@trimble/utilities';
import type {
  GenericMarker,
  MarkerState,
  MarkerCoordinateMode,
} from '../types/marker.types';
import { photoToWorldPos } from './coordinate-utils';
import { getMarkerIconPath } from '../constants/marker-visuals';

/** Default icon size in pixels */
export const DEFAULT_ICON_SIZE = 32;

/**
 * Maximum linear probing attempts when resolving hash collisions.
 * IconsPlugin requires numeric IDs, so we hash marker string IDs via `stringToSeed`.
 * Collisions are resolved by incrementing the hash until an unused slot is found.
 * 1000 probes is generous — collisions are extremely rare with good hash distribution.
 */
const MAX_COLLISION_PROBES = 1000;

/**
 * Resolves a unique numeric hash ID for a marker string ID.
 *
 * IconsPlugin identifies icons by numeric IDs. We convert string marker IDs
 * to numbers via `stringToSeed`, then use linear probing to handle the rare
 * case of two different marker IDs producing the same hash.
 *
 * @returns A unique numeric hash ID, or -1 if collision resolution failed
 *          (caller should skip rendering this marker).
 */
export const resolveMarkerHashId = (
  markerId: string,
  existingHashes: ReadonlyMap<number, string>
): number => {
  let hashId = stringToSeed(markerId);
  let probes = 0;
  while (existingHashes.has(hashId) && probes < MAX_COLLISION_PROBES) {
    hashId = (hashId + 1) >>> 0;
    probes++;
  }
  if (probes >= MAX_COLLISION_PROBES) {
    return -1;
  }
  return hashId;
};

/**
 * Compile-time exhaustive mapping from marker type to its base visual state.
 * Adding a new value to `GenericMarker['type']` without updating this record
 * will produce a TypeScript error — preventing silent fall-through.
 */
const MARKER_TYPE_TO_STATE: Record<GenericMarker['type'], MarkerState> = {
  pending: 'pending',
  saved: 'default',
};

/**
 * Determines the visual state of a marker for icon resolution.
 * Priority: pending type > selected > default.
 */
const resolveMarkerState = (
  marker: GenericMarker,
  selectedId: string | null
): MarkerState => {
  const baseState = MARKER_TYPE_TO_STATE[marker.type];
  if (baseState !== 'default') return baseState;
  if (
    selectedId !== null &&
    (marker.issueId === selectedId || marker.id === selectedId)
  )
    return 'selected';
  return baseState;
};

/**
 * Creates a `PointIcon` ready for `IconsPlugin.add()` from a `GenericMarker`.
 *
 * Resolves world-space position from `marker.spherical` (panorama) or
 * `marker.worldPosition` (model3d), then resolves icon path and hash id.
 * The caller must ensure the marker has the field required for the mode.
 */
export const createMarkerPointIcon = async (
  marker: GenericMarker,
  cameraPosition: Vector3,
  selectedId: string | null,
  iconSize: number,
  existingHashes: ReadonlyMap<number, string>,
  customIcons?: Partial<Record<MarkerState, string>>,
  mode: MarkerCoordinateMode = 'panorama'
): Promise<PointIcon> => {
  const position =
    mode === 'model3d'
      ? marker.worldPosition!.clone()
      : photoToWorldPos(marker.spherical!.pitch, marker.spherical!.yaw).add(
          cameraPosition
        );

  const state = resolveMarkerState(marker, selectedId);

  const iconPath =
    customIcons?.[state] ??
    (await getMarkerIconPath({
      priority: marker.priority,
      markerType: marker.markerType,
      isPending: state === 'pending',
      isSelected: state === 'selected',
    }));

  const hashId = resolveMarkerHashId(marker.id, existingHashes);
  return { id: hashId, iconPath, position, size: iconSize };
};
